import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Email Attachment Schema
 */
export const EmailAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  sizeBytes: z.number(),
  mimeType: z.string().optional(),
  s3Url: z.string(),
});

/**
 * Document Association Schema
 */
export const DocumentAssociationSchema = z.object({
  id: z.string(),
  document: z.object({
    id: z.string(),
    name: z.string(),
    originalFileName: z.string(),
    fileSizeBytes: z.number().nullable(),
    mimeType: z.string().nullable(),
    url: z.string(),
    contentId: z.string().nullable(),
  }),
});

/**
 * Email Classification Schema
 */
export const EmailClassificationSchema = z.object({
  id: z.string(),
  classificationResult: z.string(),
  confidenceScore: z.number(),
  reasoning: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * Email Thread Email Schema
 */
export const EmailThreadEmailSchema = z.object({
  id: z.string(),
  messageId: z.string(),
  conversationId: z.string().nullable(),
  from: z.string().nullable(),
  to: z.string(),
  cc: z.string().nullable(),
  subject: z.string().nullable(),
  textContent: z.string().nullable(),
  htmlContent: z.string().nullable(),
  attachmentCount: z.number().nullable(),
  service: z.string(),
  // When this email was actually sent/received (InboundEmail.receivedAt or
  // OutboundEmail.sentAt), falling back to createdAt when unavailable. This
  // is the timestamp the UI should render/sort by — `createdAt` below is
  // only the DB row-insert time.
  emailTimestamp: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  classification: EmailClassificationSchema.nullable(),
  attachments: z.array(EmailAttachmentSchema).optional(),
  documentAssociations: z.array(DocumentAssociationSchema).optional(),
  // Number of split-child documents (documents with a parentDocumentId)
  // associated with this email. Always returned so the UI can offer a
  // "show split documents" toggle without fetching the children.
  splitDocumentCount: z.number().optional(),
  // Split-child document associations; only populated when the request
  // sets includeSplitDocuments.
  splitDocumentAssociations: z.array(DocumentAssociationSchema).optional(),
});

/**
 * Email Thread Response Schema
 */
export const EmailThreadResponseSchema = z.object({
  conversationId: z.string(),
  emails: z.array(EmailThreadEmailSchema),
  totalCount: z.number(),
  participantEmails: z.array(z.string()),
  earliestDate: z.string(),
  latestDate: z.string(),
});

/**
 * Get Email Thread Input Schema
 */
export const GetEmailThreadInputSchema = z.object({
  conversationId: z.string().min(1),
  // When true, split-child documents (documents with a parentDocumentId)
  // are returned in splitDocumentAssociations. They are always excluded
  // from documentAssociations.
  includeSplitDocuments: z.boolean().optional(),
});

/**
 * Email Thread Contracts
 */
export const emailThreadContracts = {
  // Get an email thread by conversation ID
  getThread: oc
    .input(GetEmailThreadInputSchema)
    .output(EmailThreadResponseSchema)
    .route({
      method: "GET",
      summary: "Get an email thread",
      description:
        "Returns every inbound and outbound email in a conversation in order, with participants, classification, attachments, and linked documents; split documents optional.",
    }),
} as const;

// Export types
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;
export type DocumentAssociation = z.infer<typeof DocumentAssociationSchema>;
export type EmailClassification = z.infer<typeof EmailClassificationSchema>;
export type EmailThreadEmail = z.infer<typeof EmailThreadEmailSchema>;
export type EmailThreadResponse = z.infer<typeof EmailThreadResponseSchema>;
export type GetEmailThreadInput = z.infer<typeof GetEmailThreadInputSchema>;
